import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../globals";
import { push } from "../navigation/v2/sales/push";
import { cursoCorto } from "../utils/cursoCorto";
import { lanzarConfeti, sonarFestejo } from "./confeti";
import "./festejo.css";

/**
 * Festejo a pantalla completa cuando alguien de la rueda vende (Esteban, 2026-10-14: "gamify it para motivar a las chavas").
 * Llega por wapp-push (evento "festejo") a toda la rueda del territorio, sus supervisoras y sus directoras. Trae todo lo que
 * se enseña (nombre, foto, grupo, cursos, conteos del día), así que no pide nada a la API.
 * - Uno a la vez: si llegan varios juntos, hacen fila.
 * - Se cierra solo a los 8 s, con un toque o con Esc.
 * - Con la pestaña atrás se guarda y sale al volver, si tiene menos de 20 min; más viejo ya no festeja nada.
 * - El sonido se puede apagar (se recuerda en este navegador).
 */
export interface FestejoRanking { grupo: string; n: number; }
export interface Festejo {
	id: number;
	titulo: string;                 // "Nueva Matrícula" (Honduras) / "Nueva Inscripción" (México)
	vendedora: { id: number; nombre: string; foto: string | null };
	grupo: string;
	plantel: string;
	cursos: string[];
	clientes: number;
	reingreso?: boolean;            // se guarda, no se enseña: el reingreso de equipo cuenta como matrícula (Esteban, 2026-10-14)
	hoyElla: number;                // cuántas lleva ella hoy (con esta)
	hoyGrupo: number;               // cuántas lleva su grupo hoy
	hoyTerritorio: number;
	ranking: FestejoRanking[];      // grupos del territorio hoy, de más a menos
	en: number;                     // epoch ms
}

const DURA_MS = 8000;
const VIGENCIA_MS = 20 * 60 * 1000;
const SONIDO_KEY = "wapp.festejoSonido";

const iniciales = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
const ordinal = (n: number) => n + "ª";
const sonidoOn = (): boolean => { try { return localStorage.getItem(SONIDO_KEY) !== "off"; } catch { return true; } };

/** Frase de racha según cuántas lleva hoy. */
const racha = (n: number): string | null => {
	if (n >= 5) return "¡Imparable! " + ordinal(n) + " de hoy";
	if (n === 4) return "¡En llamas! 4ª de hoy";
	if (n === 3) return "¡Triplete! 3ª de hoy";
	if (n === 2) return "¡Doblete! 2ª de hoy";
	return null;
};

export const TarjetaFestejo: React.FC<{ f: Festejo; esYo: boolean; onCerrar: () => void; sonido: boolean; onSonido: () => void; restante?: number }> = ({ f, esYo, onCerrar, sonido, onSonido, restante }) => {
	const max = Math.max(1, ...f.ranking.map((r) => r.n));
	const frase = racha(f.hoyElla);
	return (
		<div className="fst-tarjeta" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-labelledby="fst-titulo" aria-describedby="fst-nombre">
			<div className="fst-arriba">
				<span className="fst-sello">{f.plantel}</span>
				<button className="fst-son" onClick={onSonido} aria-label={sonido ? "Silenciar festejos" : "Activar sonido de festejos"} title={sonido ? "Silenciar" : "Con sonido"}>
					{sonido ? (
						<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M19 5a10 10 0 0 1 0 14" /></svg>
					) : (
						<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z" /><path d="m23 9-6 6" /><path d="m17 9 6 6" /></svg>
					)}
				</button>
				<button className="fst-x" onClick={onCerrar} aria-label="Cerrar">×</button>
			</div>
			<h1 id="fst-titulo" className="fst-titulo">¡{f.titulo}!</h1>
			<div className="fst-foto">
				<span className="fst-aro" aria-hidden="true" />
				{f.vendedora.foto ? <img src={f.vendedora.foto} alt="" /> : <span className="fst-ini">{iniciales(f.vendedora.nombre)}</span>}
				{f.clientes > 1 ? <span className="fst-multi">×{f.clientes}</span> : null}
			</div>
			<div id="fst-nombre" className="fst-nombre">{esYo ? "¡Lo lograste, " + f.vendedora.nombre.split(" ")[0] + "!" : f.vendedora.nombre}</div>
			<div className="fst-grupo">Equipo {f.grupo}</div>
			{f.cursos.length ? (
				<div className="fst-cursos">
					{f.cursos.slice(0, 3).map((c) => <span key={c} className="fst-curso" title={c}>{cursoCorto(c) || c}</span>)}
				</div>
			) : null}
			{frase ? <div className="fst-racha"><span aria-hidden="true">🔥</span> {frase}</div> : null}
			{f.ranking.length > 1 ? (
				<div className="fst-ranking" aria-label="Equipos hoy">
					<div className="fst-rk-cab"><span>Hoy en {f.plantel}</span><b>{f.hoyTerritorio}</b></div>
					{f.ranking.slice(0, 8).map((r, i) => (
						<div key={r.grupo} className={"fst-rk" + (r.grupo === f.grupo ? " suyo" : "") + (r.n ? "" : " cero")}>
							<span className="fst-rk-pos">{r.n ? i + 1 : "–"}</span>
							<span className="fst-rk-nom">{r.grupo}</span>
							<span className="fst-rk-barra">{r.n ? <i style={{ width: Math.max(6, (r.n / max) * 100) + "%" }} /> : null}</span>
							<span className="fst-rk-n">{r.n}</span>
						</div>
					))}
				</div>
			) : (
				<div className="fst-solo">Equipo {f.grupo}: <b>{f.hoyGrupo}</b> hoy</div>
			)}
			{restante !== undefined ? <div className="fst-tiempo"><i style={{ animationDuration: DURA_MS + "ms" }} /></div> : null}
			{restante && restante > 0 ? <div className="fst-fila">+{restante} más</div> : null}
		</div>
	);
};

const FestejoMatricula: React.FC = () => {
	const campusId: number | null = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampusId) || null;
	const yo: number | null = useSelector((state: RootState) => state.rootReducer.sessionState.user?.id) || null;
	const [fila, setFila] = useState<Festejo[]>([]);
	const [aLaVista, setALaVista] = useState<boolean>(document.visibilityState === "visible");
	const [sonido, setSonido] = useState<boolean>(sonidoOn());
	const canvas = useRef<HTMLCanvasElement | null>(null);
	const vistos = useRef<Set<number>>(new Set());
	const actual = fila[0] || null;

	const recibir = useCallback((d: any) => {
		const f = d as Festejo;
		if (!f || !f.id || vistos.current.has(f.id)) return;
		vistos.current.add(f.id);
		setFila((q) => [...q, f]);
	}, []);

	useEffect(() => {
		if (!campusId) return;
		const soltar = push.usar(campusId);
		const quitar = push.on("festejo", recibir);
		return () => { quitar(); soltar(); };
	}, [campusId, recibir]);

	const cerrar = useCallback(() => setFila((q) => q.slice(1)), []);

	useEffect(() => {
		const cambio = () => setALaVista(document.visibilityState === "visible");
		document.addEventListener("visibilitychange", cambio);
		return () => document.removeEventListener("visibilitychange", cambio);
	}, []);

	// Al mostrar uno: confeti, sonido y cuenta regresiva. Con la pestaña atrás espera; si ya es viejo, se salta.
	useEffect(() => {
		if (!actual) return;
		if (Date.now() - actual.en > VIGENCIA_MS) { cerrar(); return; }
		if (!aLaVista) return;
		const parar = canvas.current ? lanzarConfeti(canvas.current) : () => undefined;
		if (sonido) sonarFestejo();
		const t = setTimeout(cerrar, DURA_MS);
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape") cerrar(); };
		window.addEventListener("keydown", esc);
		return () => { parar(); clearTimeout(t); window.removeEventListener("keydown", esc); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [actual, aLaVista, cerrar]);

	const cambiarSonido = () => {
		const v = !sonido;
		setSonido(v);
		try { localStorage.setItem(SONIDO_KEY, v ? "on" : "off"); } catch { /* nada */ }
	};

	if (!actual || !aLaVista) return null;
	return (
		<div className="fst-fondo" onClick={cerrar}>
			<canvas ref={canvas} className="fst-confeti" aria-hidden="true" />
			<TarjetaFestejo key={actual.id} f={actual} esYo={!!yo && yo === actual.vendedora.id} onCerrar={cerrar}
				sonido={sonido} onSonido={cambiarSonido} restante={fila.length - 1} />
		</div>
	);
};

export default FestejoMatricula;
